/**
 * AskAIDialog — "Ask AI from this node" (Claude Sonnet 4.5).
 *
 * Three modes:
 *   1. suggest_children — AI returns 2–5 candidate next-nodes; user picks
 *      which ones to insert into the canvas.
 *   2. rewrite          — AI proposes a cleaner label + description; user
 *      accepts or ignores.
 *   3. chat             — freeform Q&A, node context is passed automatically.
 *
 * Props:
 *   open, onClose
 *   diagramId, node      — { id, data:{ label, description, richText, shape } }
 *   onInsertChildren(sug)— (array of {label, shape, description}) => void
 *   onApplyRewrite(rw)   — ({label, description}) => void
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, ChevronDown, Wand2, MessagesSquare, GitBranchPlus, Loader2 } from "lucide-react";

const MODES = [
  { key: "suggest_children", label: "Suggest next steps", icon: GitBranchPlus, desc: "AI proposes child nodes to add after this one." },
  { key: "rewrite",          label: "Rewrite this node",   icon: Wand2,         desc: "AI improves the label and description." },
  { key: "chat",             label: "Ask a question",      icon: MessagesSquare, desc: "Freeform Q&A with node context." },
];

function stripHtml(html) {
  const el = document.createElement("div");
  el.innerHTML = html || "";
  return el.textContent || el.innerText || "";
}

export function AskAIDialog({ open, onClose, diagramId, node, onInsertChildren, onApplyRewrite }) {
  const [mode, setMode] = useState("suggest_children");
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // shape depends on mode
  const [selectedIdx, setSelectedIdx] = useState({}); // for suggest_children

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  const nodeCtx = {
    label: node?.data?.label || "",
    description: node?.data?.description || "",
    shape: node?.data?.shape || "process",
    richText: stripHtml(node?.data?.richText || ""),
  };

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const body = {
        mode,
        node_id: node?.id,
        node_context: nodeCtx,
        prompt: mode === "chat" ? prompt.trim() : undefined,
      };
      const { data } = await api.post(`/diagrams/${diagramId}/nodes/ask-ai`, body);
      setResult(data);
      if (mode === "suggest_children") {
        const m = {};
        (data.nodes || []).forEach((_, i) => { m[i] = true; });
        setSelectedIdx(m);
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, "Ask AI failed"));
    } finally {
      setBusy(false);
    }
  };

  const insertPicked = () => {
    const picks = (result?.nodes || []).filter((_, i) => selectedIdx[i]);
    if (!picks.length) return toast.error("Select at least one suggestion");
    onInsertChildren?.(picks);
    toast.success(`Inserted ${picks.length} node(s)`);
    onClose();
  };
  const applyRewrite = () => {
    if (!result) return;
    onApplyRewrite?.({ label: result.label, description: result.description });
    toast.success("Rewrite applied");
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center" onClick={onClose} data-testid="ask-ai-dialog">
      <div className="bg-background border w-full max-w-lg p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="font-semibold flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-500" /> Ask AI from this node
          </div>
          <button className="text-muted-foreground" onClick={onClose}>✕</button>
        </div>

        <div className="grid grid-cols-3 gap-1">
          {MODES.map((m) => {
            const Icon = m.icon;
            const active = mode === m.key;
            return (
              <button
                key={m.key}
                onClick={() => { setMode(m.key); setResult(null); }}
                className={`flex flex-col items-center gap-1 p-2 border text-xs ${active ? "bg-foreground text-background border-foreground" : "hover:border-foreground"}`}
                data-testid={`ask-ai-mode-${m.key}`}
              >
                <Icon className="w-4 h-4" />
                <div className="font-medium">{m.label}</div>
              </button>
            );
          })}
        </div>

        <div className="text-[11px] text-muted-foreground">
          {MODES.find((m) => m.key === mode)?.desc}
          <div className="mt-1 border p-2 bg-neutral-50">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Node context</div>
            <div className="font-medium truncate">{nodeCtx.label || "(untitled)"}</div>
            {nodeCtx.description && <div className="text-muted-foreground truncate">{nodeCtx.description}</div>}
          </div>
        </div>

        {mode === "chat" && (
          <Textarea
            rows={3}
            placeholder="Ask anything about this step…"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            className="rounded-none text-sm"
            data-testid="ask-ai-prompt"
          />
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" className="rounded-none" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="rounded-none" onClick={run} disabled={busy || (mode === "chat" && !prompt.trim())} data-testid="ask-ai-run">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
            {busy ? "Thinking…" : "Ask Claude"}
          </Button>
        </div>

        {result && mode === "suggest_children" && (
          <div className="border-t pt-3 space-y-2 max-h-[40vh] overflow-auto">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Suggestions ({(result.nodes || []).length})</div>
            {(result.nodes || []).map((n, i) => (
              <label key={i} className="flex items-start gap-2 border p-2 text-xs cursor-pointer hover:border-foreground" data-testid={`ask-ai-suggestion-${i}`}>
                <input type="checkbox" checked={!!selectedIdx[i]} onChange={(e) => setSelectedIdx({ ...selectedIdx, [i]: e.target.checked })} />
                <div>
                  <div className="font-medium">{n.label} <span className="text-[10px] text-muted-foreground uppercase">· {n.shape}</span></div>
                  <div className="text-muted-foreground">{n.description}</div>
                </div>
              </label>
            ))}
            <Button className="rounded-none w-full" onClick={insertPicked} data-testid="ask-ai-insert">
              <ChevronDown className="w-4 h-4 mr-1" /> Insert selected as child nodes
            </Button>
          </div>
        )}

        {result && mode === "rewrite" && (
          <div className="border-t pt-3 space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Proposed rewrite</div>
            <div className="border p-2 text-xs">
              <div className="font-medium">{result.label}</div>
              <div className="text-muted-foreground">{result.description}</div>
            </div>
            <Button className="rounded-none w-full" onClick={applyRewrite} data-testid="ask-ai-apply-rewrite">
              Apply rewrite to node
            </Button>
          </div>
        )}

        {result && mode === "chat" && (
          <div className="border-t pt-3 space-y-2 max-h-[35vh] overflow-auto">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Claude&apos;s answer</div>
            <div className="border p-2 text-xs whitespace-pre-wrap">{result.answer}</div>
          </div>
        )}
      </div>
    </div>
  );
}
